import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  FilmStrip,
  MusicNotes,
  Play,
  Spinner,
  Star,
} from '@phosphor-icons/react';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert common video URLs (YouTube, Vimeo) to proper embed URLs
const toEmbedUrl = (url) => {
  if (!url) return url;
  try {
    let m = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
    m = url.match(/youtube\.com\/watch\?(?:.*&)?v=([A-Za-z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
    m = url.match(/youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
    m = url.match(/youtube\.com\/live\/([A-Za-z0-9_-]{11})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0`;
    // Already an embed URL — add autoplay if missing so a re-mount actually
    // starts the new video without the user clicking Play again.
    m = url.match(/youtube\.com\/embed\/([A-Za-z0-9_-]{11})/);
    if (m) {
      if (/[?&]autoplay=/.test(url)) return url;
      return url + (url.includes('?') ? '&' : '?') + 'autoplay=1&rel=0';
    }
    m = url.match(/vimeo\.com\/(\d+)/);
    if (m && !url.includes('player.vimeo.com')) return `https://player.vimeo.com/video/${m[1]}?autoplay=1`;
    return url;
  } catch { return url; }
};

const getThumbnail = (url) => {
  if (!url) return null;
  const m = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/) ||
            url.match(/youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)([A-Za-z0-9_-]{11})/);
  if (m) return `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg`;
  return null;
};

// ── Music rating block: Ritmo (everyone) + Aussprache (teacher only) ─────
const MusicRating = ({ video, onUpdated, lang, isTeacher }) => {
  const [savingField, setSavingField] = useState(null);
  const [hover, setHover] = useState({ ritmo: 0, pronunciation: 0 });

  const rate = async (field, value) => {
    if (savingField) return;
    setSavingField(field);
    try {
      const res = await axios.post(
        `${API}/videos/${video.video_id}/rating`,
        { [field]: value },
        { withCredentials: true }
      );
      toast.success(lang === 'es' ? `¡Gracias! ${value}/5` : `Danke! ${value}/5`);
      onUpdated && onUpdated(video.video_id, res.data);
    } catch {
      toast.error(lang === 'es' ? 'Error al guardar' : 'Speichern fehlgeschlagen');
    } finally { setSavingField(null); }
  };

  const Row = ({ field, label, my, avg }) => (
    <div className="flex items-center justify-between gap-3 flex-wrap">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider font-bold text-[#78716C]">{label}</p>
        <div
          className="flex items-center gap-0.5 mt-0.5"
          onMouseLeave={() => setHover(h => ({ ...h, [field]: 0 }))}
        >
          {[1, 2, 3, 4, 5].map(n => {
            const filled = (hover[field] || my || 0) >= n;
            return (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(h => ({ ...h, [field]: n }))}
                onClick={() => rate(field, n)}
                disabled={savingField === field}
                aria-label={`${n}/5`}
                className="p-0.5 transition-transform hover:scale-110 disabled:opacity-60"
                data-testid={`music-star-${field}-${video.video_id}-${n}`}
              >
                <Star size={20} weight={filled ? 'fill' : 'regular'} className={filled ? 'text-amber-500' : 'text-stone-300'} />
              </button>
            );
          })}
        </div>
      </div>
      <div className="text-right">
        <p className="text-[10px] text-[#A8A29E] uppercase tracking-wider">{lang === 'es' ? 'Media' : 'Schnitt'}</p>
        <p className="font-bold text-[#292524] text-base leading-none">
          {avg ? avg.toFixed(1) : '—'}
          <span className="text-xs text-[#78716C] font-normal ml-1">/ 5</span>
        </p>
      </div>
    </div>
  );

  return (
    <div className="mt-3 rounded-lg bg-stone-50 border border-stone-200 px-3 py-2.5 space-y-2.5" data-testid={`music-rating-${video.video_id}`}>
      <Row
        field="ritmo"
        label={lang === 'es' ? 'Ritmo' : 'Rhythmus'}
        my={video.my_ritmo || 0}
        avg={video.rating_avg_ritmo || 0}
      />
      {isTeacher && (
        <div className="pt-2 border-t border-stone-200">
          <Row
            field="pronunciation"
            label={lang === 'es' ? 'Pronunciación · solo profesor' : 'Aussprache · nur Lehrer'}
            my={video.my_pronunciation || 0}
            avg={video.rating_avg_pronunciation || 0}
          />
        </div>
      )}
      {!isTeacher && (video.rating_avg_pronunciation || 0) > 0 && (
        <div className="pt-2 border-t border-stone-200 flex items-center justify-between text-xs">
          <span className="text-[#78716C]">
            {lang === 'es' ? 'Pronunciación (profesor)' : 'Aussprache (Lehrer)'}
          </span>
          <span className="font-bold text-[#292524]">
            {video.rating_avg_pronunciation.toFixed(1)} <span className="text-[#78716C] font-normal">/ 5</span>
          </span>
        </div>
      )}
    </div>
  );
};

const VideosPage = () => {
  const { lang } = useLang();
  const { user } = useAuth();
  const isTeacher = user?.role === 'admin' || user?.role === 'teacher';
  const [tab, setTab] = useState('miniserie'); // 'miniserie' | 'music'
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  // All media (Musik + Serien) is shown to every student regardless of CEFR
  // level — the small "level badge" chip on cards is also hidden below.
  const [activeVideo, setActiveVideo] = useState(null);

  const loadVideos = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/videos?limit=50&category=${tab}`;
      const res = await axios.get(url, { withCredentials: true });
      setVideos(res.data);
    } catch { toast.error('Fehler'); }
    finally { setLoading(false); }
  }, [tab]);

  useEffect(() => { loadVideos(); }, [loadVideos]);

  // When a rating succeeds, refresh just that video's numbers locally.
  const onRatingUpdated = (videoId, summary) => {
    setVideos(prev => prev.map(v => v.video_id === videoId ? { ...v, ...summary } : v));
    if (activeVideo?.video_id === videoId) setActiveVideo(av => ({ ...av, ...summary }));
  };

  const isMusic = tab === 'music';

  return (
    <div className="space-y-6" data-testid="media-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#292524] flex items-center gap-2">
            <FilmStrip size={28} weight="duotone" className="text-[#AB1C24]" />
            Media
          </h1>
          <p className="text-[#57534E] mt-1">
            {isMusic
              ? (lang === 'es' ? 'Aprende español con música y valora cada canción.' : 'Lerne Spanisch mit Musik und bewerte jeden Song.')
              : (lang === 'es' ? 'Aprende español con nuestra videoserie.' : 'Lerne Spanisch mit unserer Videoserie.')}
          </p>
        </div>
      </div>

      {/* Category tabs */}
      <div className="inline-flex bg-stone-100 rounded-xl p-1" data-testid="media-tabs">
        {[
          { v: 'miniserie', label: 'Miniserie', icon: FilmStrip },
          { v: 'music', label: lang === 'es' ? 'Música' : 'Musik', icon: MusicNotes },
        ].map(({ v, label, icon: Ic }) => (
          <button
            key={v}
            onClick={() => { setTab(v); setActiveVideo(null); }}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              tab === v
                ? 'bg-white text-[#AB1C24] shadow'
                : 'text-[#57534E] hover:text-[#292524]'
            }`}
            data-testid={`media-tab-${v}`}
          >
            <Ic size={16} weight="duotone" />
            {label}
          </button>
        ))}
      </div>

      {/* Active Player */}
      {activeVideo && (
        <div className="bg-[#1a1a2e] rounded-2xl overflow-hidden" data-testid="video-player" ref={(el) => { if (el && activeVideo) { try { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { /* noop */ } } }}>
          <div className="aspect-video w-full">
            <iframe
              // Force React to UNMOUNT the previous iframe and mount a fresh
              // one whenever the user picks a different video. Without this
              // key, React only updates the `src` attribute on the existing
              // iframe, which the YouTube player sometimes ignores — leaving
              // the previously-loaded video playing. The key fixes the
              // "only the first music video works" bug reported by users.
              key={activeVideo.video_id}
              src={toEmbedUrl(activeVideo.video_url)}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
              title={activeVideo.title}
            />
          </div>
          <div className="p-6">
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              {!isMusic && <span className="text-white/60 text-sm">Folge {activeVideo.episode}</span>}
              {isMusic && activeVideo.artist && <span className="text-white/60 text-sm">{activeVideo.artist}</span>}
              {activeVideo.duration && <span className="text-white/60 text-sm">{activeVideo.duration}</span>}
            </div>
            <h2 className="font-heading text-xl font-bold text-white mb-1">{activeVideo.title}</h2>
            <p className="text-white/70">{activeVideo.description}</p>
            {isMusic && (
              <div className="mt-4 -mx-2">
                <MusicRating video={activeVideo} onUpdated={onRatingUpdated} lang={lang} isTeacher={isTeacher} />
              </div>
            )}
            <Button onClick={() => setActiveVideo(null)} variant="outline" className="mt-4 text-white border-white/30 hover:bg-white/10" data-testid="close-video-btn">
              {lang === 'es' ? 'Cerrar' : 'Schließen'}
            </Button>
          </div>
        </div>
      )}

      {/* Grid */}
      {loading ? (
        <div className="flex justify-center py-12"><Spinner size={32} className="animate-spin text-[#AB1C24]" /></div>
      ) : videos.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {videos.map(video => (
            <div key={video.video_id} className="bg-white rounded-2xl border border-[#D6D3D1] overflow-hidden hover:shadow-md transition-shadow flex flex-col" data-testid={`video-card-${video.video_id}`}>
              <div
                className="h-40 bg-gradient-to-br from-[#5B8C9A] to-[#4A7A87] flex items-center justify-center relative overflow-hidden cursor-pointer group"
                onClick={() => {
                  setActiveVideo(video);
                  // Award +5 points the first time the user plays this media
                  // — backend is idempotent so re-plays don't double-count.
                  axios.post(`${API}/videos/${video.video_id}/play`, {}, { withCredentials: true })
                    .then(r => { if (r.data?.awarded) toast.success(lang === 'es' ? '¡+5 puntos!' : '+5 Punkte!'); })
                    .catch(() => {});
                }}
              >
                {getThumbnail(video.video_url) && (
                  <img
                    src={getThumbnail(video.video_url)}
                    alt={video.title}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                )}
                <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
                <div className="relative w-16 h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play size={32} className="text-white ml-1" weight="fill" />
                </div>
                {!isMusic && (
                  <span className="absolute top-3 left-3 bg-black/40 text-white text-xs px-2 py-1 rounded-lg z-10">
                    Folge {video.episode}
                  </span>
                )}
                {video.duration && (
                  <span className="absolute bottom-3 right-3 bg-black/40 text-white text-xs px-2 py-1 rounded-lg z-10">
                    {video.duration}
                  </span>
                )}
              </div>
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs text-[#78716C]">{isMusic ? (video.artist || 'Música') : video.series}</span>
                </div>
                <h3 className="font-heading font-semibold text-[#292524] mb-1 line-clamp-1">{video.title}</h3>
                <p className="text-sm text-[#78716C] line-clamp-2 flex-1">{video.description}</p>
                {isMusic && (
                  <MusicRating video={video} onUpdated={onRatingUpdated} lang={lang} isTeacher={isTeacher} />
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-2xl border border-[#D6D3D1]">
          {isMusic ? <MusicNotes size={48} className="mx-auto text-[#78716C] mb-4" /> : <FilmStrip size={48} className="mx-auto text-[#78716C] mb-4" />}
          <p className="text-[#78716C]">
            {isMusic
              ? (lang === 'es' ? 'Aún no hay música. El profesor puede añadir canciones.' : 'Noch keine Musik. Der Lehrer kann Songs hinzufügen.')
              : (lang === 'es' ? 'Aún no hay videos. El profesor puede añadirlos.' : 'Noch keine Videos verfügbar. Der Lehrer kann Videos hinzufügen.')}
          </p>
        </div>
      )}
    </div>
  );
};

export default VideosPage;
